import { useRef, useState, type ReactNode } from "react";
import { File, Images, Mic, Paperclip, SendHorizontal, Workflow } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Fluxo } from "@/hooks/useFluxos";
import { MobileAudioRecorder } from "./MobileAudioRecorder";
import { MobileFluxosSheet } from "./MobileFluxosSheet";
import { MobileSheet } from "./MobileSheet";

interface MobileMessageInputProps {
  message: string;
  setMessage: (message: string) => void;
  onSendMessage: () => void;
  onSendAudio: (audioUrl: string) => Promise<boolean>;
  onSendImage: (imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendVideo: (videoBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendDocument: (documentBase64: string, fileName: string, caption?: string) => Promise<boolean>;
  /** Ausente quando a conversa não pode disparar fluxos; esconde a opção no anexar. */
  onSendFluxo?: (fluxo: Fluxo) => void;
  sending?: boolean;
  /** Barra de citação acima do campo; ausente quando não se está respondendo. */
  replyBar?: ReactNode;
}

const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const MobileMessageInput = ({
  message,
  setMessage,
  onSendMessage,
  onSendAudio,
  onSendImage,
  onSendVideo,
  onSendDocument,
  onSendFluxo,
  sending = false,
  replyBar = null,
}: MobileMessageInputProps) => {
  const { t } = useTranslation("chat");
  const [attachOpen, setAttachOpen] = useState(false);
  const [fluxosOpen, setFluxosOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasText = message.trim().length > 0;
  const canSend = hasText && !sending;

  const handleSend = () => {
    if (!canSend) return;
    onSendMessage();
    // Reset da altura do textarea após enviar
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const openPicker = (ref: { current: HTMLInputElement | null }) => {
    setAttachOpen(false);
    // Pequeno delay para o sheet fechar antes do picker nativo abrir
    setTimeout(() => ref.current?.click(), 150);
  };

  const handleMediaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isMp4Video = file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");

    if (!isImage && !isMp4Video) {
      alert(t("input.errPickImageVideo"));
      return;
    }

    const isVideo = isMp4Video && !isImage;
    const maxSize = isVideo ? 64 * 1024 * 1024 : 16 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(isVideo ? "O vídeo deve ter no máximo 64MB" : "A imagem deve ter no máximo 16MB");
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      if (isVideo) {
        await onSendVideo(base64, "", file.name);
      } else {
        await onSendImage(base64, "", file.name);
      }
    } catch (error) {
      console.error("Erro ao enviar mídia:", error);
      alert(t("input.errSendMedia"));
    } finally {
      if (mediaInputRef.current) mediaInputRef.current.value = "";
    }
  };

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(t("input.errDocumentSize"));
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      await onSendDocument(base64, file.name, "");
    } catch (error) {
      console.error("Erro ao enviar documento:", error);
      alert(t("input.errSendDocument"));
    } finally {
      if (documentInputRef.current) documentInputRef.current.value = "";
    }
  };

  return (
    <div className="shrink-0 border-t border-border bg-card px-3 pb-[max(env(safe-area-inset-bottom),10px)] pt-2">
      <input
        ref={mediaInputRef}
        type="file"
        accept="image/*,video/mp4,.mp4"
        onChange={handleMediaChange}
        className="hidden"
      />
      <input
        ref={documentInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
        onChange={handleDocumentChange}
        className="hidden"
      />

      {recording ? (
        <MobileAudioRecorder onSendAudio={onSendAudio} onClose={() => setRecording(false)} />
      ) : (
        <div className="flex flex-col gap-2">
          {replyBar}
          <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => setAttachOpen(true)}
            disabled={sending}
            aria-label={t("input.attachFile")}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-hover disabled:opacity-50"
          >
            <Paperclip className="h-5 w-5" />
          </button>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInput}
            placeholder={t("input.placeholderShort")}
            rows={1}
            className="max-h-[120px] min-h-[44px] flex-1 resize-none rounded-3xl border border-border bg-background px-4 py-[11px] text-[15px] leading-snug text-foreground outline-none placeholder:text-muted-foreground focus:border-accent-blue"
          />

          {hasText ? (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              aria-label={t("input.sendAria")}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-blue text-white shadow-sm transition-all active:scale-95 disabled:opacity-40"
            >
              {sending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <SendHorizontal className="h-5 w-5" />
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setRecording(true)}
              disabled={sending}
              aria-label={t("input.recordAria")}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-blue text-white shadow-sm transition-all active:scale-95 disabled:opacity-40"
            >
              <Mic className="h-5 w-5" />
            </button>
          )}
          </div>
        </div>
      )}

      <MobileSheet open={attachOpen} onOpenChange={setAttachOpen} title={t("input.attach")}>
        <div className="flex flex-col gap-1 pb-2">
          <button
            type="button"
            onClick={() => openPicker(mediaInputRef)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left transition-colors active:bg-hover"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2B9AF3]/10">
              <Images className="h-5 w-5 text-[#2B9AF3]" />
            </span>
            <div>
              <p className="text-[15px] font-medium text-foreground">{t("input.photosVideos")}</p>
              <p className="text-[12px] text-muted-foreground">{t("input.photosVideosHint")}</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => openPicker(documentInputRef)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left transition-colors active:bg-hover"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#7C5CFC]/10">
              <File className="h-5 w-5 text-[#7C5CFC]" />
            </span>
            <div>
              <p className="text-[15px] font-medium text-foreground">{t("input.document")}</p>
              <p className="text-[12px] text-muted-foreground">{t("input.documentHint")}</p>
            </div>
          </button>
          {onSendFluxo && (
            <button
              type="button"
              onClick={() => {
                setAttachOpen(false);
                // Espera o sheet de anexos fechar antes de abrir o de fluxos:
                // dois drawers abertos ao mesmo tempo brigam pelo scroll lock.
                setTimeout(() => setFluxosOpen(true), 150);
              }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left transition-colors active:bg-hover"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#20B26C]/10">
                <Workflow className="h-5 w-5 text-[#20B26C]" />
              </span>
              <div>
                <p className="text-[15px] font-medium text-foreground">{t("pickers.flows")}</p>
                <p className="text-[12px] text-muted-foreground">{t("input.flowsHint")}</p>
              </div>
            </button>
          )}
        </div>
      </MobileSheet>

      {onSendFluxo && (
        <MobileFluxosSheet open={fluxosOpen} onOpenChange={setFluxosOpen} onSelect={onSendFluxo} />
      )}
    </div>
  );
};
